import { useEffect, useRef } from 'react';
import {
  DEFAULT_FILTERS_STATE,
  MAX_INFLATION_PERCENT,
  toLegacyInflationRange,
  toMinimumInflationPercent,
  type PaymentOption,
  type SliderBounds,
} from '@/features/shop-insurance';
import type { UseFormSetValue } from 'react-hook-form';

interface UseSliderBoundsSyncParams {
  sliderBounds: SliderBounds;
  paymentOption: PaymentOption;
  reset: (values: Record<string, unknown>) => void;
  getValues: (name: string) => unknown;
  setValue: UseFormSetValue<any>;
  savedFilterDefaults?: ShopInsuranceFilterDefaults | null;
}

export function useSliderBoundsSync({
  sliderBounds,
  paymentOption,
  reset,
  getValues,
  setValue,
  savedFilterDefaults,
}: UseSliderBoundsSyncParams) {
  const prevBoundsRef = useRef<{
    min: number;
    max: number;
    isCalculatedFromQuotes: boolean;
  } | null>(null);
  const initialResetDoneRef = useRef(false);
  const prevPaymentOptionRef = useRef<PaymentOption>(paymentOption);

  const savedFormValues = savedFilterDefaults
    ? {
        benefitDuration:
          savedFilterDefaults.benefitDuration ?? DEFAULT_FILTERS_STATE.benefitDuration,
        inflationProtection:
          toMinimumInflationPercent(savedFilterDefaults.inflationProtection) ??
          DEFAULT_FILTERS_STATE.inflationProtection,
        inflationProtectionRange:
          toLegacyInflationRange(
            savedFilterDefaults.inflationProtection,
            MAX_INFLATION_PERCENT,
          ) ?? DEFAULT_FILTERS_STATE.inflationProtectionRange,
        inflationTypeFilter:
          savedFilterDefaults.inflationType ?? DEFAULT_FILTERS_STATE.inflationTypeFilter,
        useCustomPremiumInput: savedFilterDefaults.useCustomPremiumInput ?? false,
      }
    : { ...DEFAULT_FILTERS_STATE, useCustomPremiumInput: false };

  // Effect 1: When slider bounds change (e.g. after tab switch fetches new quotes),
  // only adjust premium. Do NOT touch other filters.
  // Uses saved filter defaults as the preferred premium when current value is out of range.
  useEffect(() => {
    if (sliderBounds.isCalculatedFromQuotes) {
      const boundsChanged =
        !prevBoundsRef.current?.isCalculatedFromQuotes ||
        prevBoundsRef.current.min !== sliderBounds.min ||
        prevBoundsRef.current.max !== sliderBounds.max;

      if (boundsChanged) {
        const currentPremium = getValues('premium') as number;
        const outOfRange =
          typeof currentPremium !== 'number' ||
          isNaN(currentPremium) ||
          currentPremium < sliderBounds.min ||
          currentPremium > sliderBounds.max;

        if (outOfRange) {
          // Current premium doesn't fit — try saved value, then fall back to default
          const savedPremium = savedFilterDefaults?.premium;
          if (
            savedPremium != null &&
            savedPremium >= sliderBounds.min &&
            savedPremium <= sliderBounds.max
          ) {
            setValue('premium', savedPremium);
          } else {
            setValue('premium', sliderBounds.defaultValue);
          }
        }
        // If current premium is in range, leave it as-is (respects in-session changes)
      }
    }

    prevBoundsRef.current = {
      min: sliderBounds.min,
      max: sliderBounds.max,
      isCalculatedFromQuotes: sliderBounds.isCalculatedFromQuotes,
    };
  }, [sliderBounds, getValues, setValue, savedFilterDefaults]);

  // Effect 2: Initial form setup (once) using saved defaults.
  useEffect(() => {
    if (!sliderBounds.isCalculatedFromQuotes || initialResetDoneRef.current) return;
    initialResetDoneRef.current = true;

    // 20-Pay is an annual outlay like 10-Pay, not a lump sum, so it takes the
    // annual-scale default. Its quotes are held at their scraped premium by the
    // uncalibrated-band guard, so this only sets where the slider starts.
    const isAnnualOutlayOption = paymentOption === '10-Pay' || paymentOption === '20-Pay';
    const fallbackPremium = isAnnualOutlayOption ? 10000 : 100000;
    const defaultPremium = Math.max(sliderBounds.min, fallbackPremium);

    if (savedFilterDefaults?.premium != null) {
      const savedPremium = savedFilterDefaults.premium;
      const inRange = savedPremium >= sliderBounds.min && savedPremium <= sliderBounds.max;
      reset({
        ...savedFormValues,
        premium: inRange ? savedPremium : defaultPremium,
      });
    } else {
      reset({
        ...DEFAULT_FILTERS_STATE,
        useCustomPremiumInput: false,
        premium: defaultPremium,
      });
    }
    // savedFilterDefaults intentionally excluded — it's available synchronously
    // from localStorage on first render, and including it would cause the else
    // branch to reset filters to system defaults on every session refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderBounds.isCalculatedFromQuotes]);

  // Effect 3: When payment option changes, only update premium to the new default.
  useEffect(() => {
    if (!initialResetDoneRef.current) return;
    if (paymentOption === prevPaymentOptionRef.current) return;
    prevPaymentOptionRef.current = paymentOption;

    if (sliderBounds.isCalculatedFromQuotes) {
      // 20-Pay is an annual outlay like 10-Pay, not a lump sum, so it takes the
    // annual-scale default. Its quotes are held at their scraped premium by the
    // uncalibrated-band guard, so this only sets where the slider starts.
    const isAnnualOutlayOption = paymentOption === '10-Pay' || paymentOption === '20-Pay';
    const fallbackPremium = isAnnualOutlayOption ? 10000 : 100000;
      setValue('premium', Math.max(sliderBounds.min, fallbackPremium));
    }
  }, [paymentOption, sliderBounds.isCalculatedFromQuotes, sliderBounds.min, setValue]);
}
